import { useSeoMeta } from '@unhead/react';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { Card, CardContent } from '@/components/ui/card';
import { useAuthor } from '@/hooks/useAuthor';
import { getDisplayName } from '@/lib/getDisplayName';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import {
  ABOUT_IMAGE,
  ABOUT_INTRO,
  STAFF,
  getShowcasePubkey,
  type StaffMember,
} from '@/lib/showcase';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || '?';
}

function StaffCard({ member }: { member: StaffMember }) {
  const photo = member.photo ? sanitizeUrl(member.photo) : undefined;

  return (
    <Card className="h-full">
      <CardContent className="flex flex-col items-center p-6 text-center">
        <div className="aspect-square w-48 shrink-0 overflow-hidden rounded-xl bg-muted shadow-sm sm:w-56">
          {photo ? (
            <img
              src={photo}
              alt={member.name}
              className="size-full object-cover object-top"
            />
          ) : (
            <div className="flex size-full items-center justify-center text-4xl font-semibold text-muted-foreground">
              {initials(member.name)}
            </div>
          )}
        </div>

        <h3 className="mt-5 font-display text-xl font-bold">{member.name}</h3>
        <p className="mt-1 text-xs font-semibold uppercase tracking-widest text-accent">
          {member.role}
        </p>
        <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground">
          {member.bio}
        </p>
      </CardContent>
    </Card>
  );
}

export default function About() {
  const pubkey = getShowcasePubkey();
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const orgName = metadata?.display_name || getDisplayName(metadata, pubkey);

  useSeoMeta({
    title: `About · ${orgName}`,
    description: ABOUT_INTRO[0]?.slice(0, 160),
    ogTitle: `About · ${orgName}`,
    ogDescription: ABOUT_INTRO[0]?.slice(0, 160),
    ogImage: metadata?.banner || metadata?.picture,
  });

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      <main className="mx-auto max-w-4xl px-4 py-12 sm:py-16">
        {/* Vision & Mission */}
        <section className="text-center">
          <h1 className="font-display text-3xl sm:text-4xl font-bold">
            About Us
          </h1>
          <div className="mt-8 flex justify-center">
            <img
              src={ABOUT_IMAGE}
              alt="Vanuatu Bible Translation vision and mission"
              className="w-full max-w-2xl rounded-xl shadow-md"
              loading="eager"
            />
          </div>
        </section>

        {/* Staff bios */}
        <section className="mt-16">
          <h2 className="text-center font-display text-2xl font-bold">
            Meet the Team
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {STAFF.map((member, i) => (
              <StaffCard key={`${member.name}-${i}`} member={member} />
            ))}
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
